import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {AdminApiService} from '../../admin-api.service';
import {AdminFeatureFlag} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {AdminPageActionsService} from '../../core/admin-page-actions.service';
import {UiButtonComponent, UiToastService} from 'freyr-ui';

interface FeatureDefinition {
  key: string;
  titleKey: string;
  descriptionKey: string;
  groupKey: string;
  route: string;
}

@Component({
  selector: 'app-feature-flags-page',
  standalone: true,
  imports: [CommonModule, UiButtonComponent, AdminTranslatePipe],
  templateUrl: './feature-flags-page.component.html',
  styleUrl: './feature-flags-page.component.scss'
})
export class FeatureFlagsPageComponent implements OnInit, OnDestroy {
  private readonly api = inject(AdminApiService);
  private readonly i18n = inject(AdminI18nService);
  private readonly pageActions = inject(AdminPageActionsService);
  private readonly toast = inject(UiToastService);
  private actionsSubscription?: Subscription;

  readonly statuses: Array<AdminFeatureFlag['status']> = ['ENABLED', 'DISABLED', 'COMING_SOON'];
  readonly features: FeatureDefinition[] = [
    { key: 'site.main_access', titleKey: 'admin.features.items.site.mainAccess.title', descriptionKey: 'admin.features.items.site.mainAccess.description', groupKey: 'admin.features.groups.platform', route: '/' },
    { key: 'dashboard.commands', titleKey: 'admin.features.items.dashboard.commands.title', descriptionKey: 'admin.features.items.dashboard.commands.description', groupKey: 'admin.features.groups.twitch', route: '/dashboard/commands' },
    { key: 'dashboard.auto_messages', titleKey: 'admin.features.items.dashboard.autoMessages.title', descriptionKey: 'admin.features.items.dashboard.autoMessages.description', groupKey: 'admin.features.groups.twitch', route: '/dashboard/auto-messages' },
    { key: 'dashboard.moderation', titleKey: 'admin.features.items.dashboard.moderation.title', descriptionKey: 'admin.features.items.dashboard.moderation.description', groupKey: 'admin.features.groups.twitch', route: '/dashboard/moderation' },
    { key: 'dashboard.planning', titleKey: 'admin.features.items.dashboard.planning.title', descriptionKey: 'admin.features.items.dashboard.planning.description', groupKey: 'admin.features.groups.twitch', route: '/dashboard/planning' },
    { key: 'dashboard.tools', titleKey: 'admin.features.items.dashboard.tools.title', descriptionKey: 'admin.features.items.dashboard.tools.description', groupKey: 'admin.features.groups.creatorTools', route: '/dashboard/tools' },
    { key: 'dashboard.tools.levels', titleKey: 'admin.features.items.dashboard.toolsLevels.title', descriptionKey: 'admin.features.items.dashboard.toolsLevels.description', groupKey: 'admin.features.groups.creatorTools', route: '/dashboard/tools/level' },
    { key: 'dashboard.tools.followers', titleKey: 'admin.features.items.dashboard.toolsFollowers.title', descriptionKey: 'admin.features.items.dashboard.toolsFollowers.description', groupKey: 'admin.features.groups.creatorTools', route: '/dashboard/tools/level/followers' },
    { key: 'dashboard.tools.panel_streamer', titleKey: 'admin.features.items.dashboard.toolsPanelStreamer.title', descriptionKey: 'admin.features.items.dashboard.toolsPanelStreamer.description', groupKey: 'admin.features.groups.creatorTools', route: '/dashboard/tools/panel-streamer' },
    { key: 'dashboard.spotify', titleKey: 'admin.features.items.dashboard.spotify.title', descriptionKey: 'admin.features.items.dashboard.spotify.description', groupKey: 'admin.features.groups.integrations', route: '/dashboard/spotify' },
    { key: 'dashboard.discord', titleKey: 'admin.features.items.dashboard.discord.title', descriptionKey: 'admin.features.items.dashboard.discord.description', groupKey: 'admin.features.groups.integrations', route: '/dashboard/discord' },
    { key: 'dashboard.bot_imports', titleKey: 'admin.features.items.dashboard.botImports.title', descriptionKey: 'admin.features.items.dashboard.botImports.description', groupKey: 'admin.features.groups.integrations', route: '/dashboard/integrations/imports' },
    { key: 'dashboard.youtube', titleKey: 'admin.features.items.dashboard.youtube.title', descriptionKey: 'admin.features.items.dashboard.youtube.description', groupKey: 'admin.features.groups.integrations', route: '/dashboard/youtube' },
    { key: 'dashboard.overlays', titleKey: 'admin.features.items.dashboard.overlays.title', descriptionKey: 'admin.features.items.dashboard.overlays.description', groupKey: 'admin.features.groups.creatorTools', route: '/dashboard/overlays' },
    { key: 'bot.twitch.helix_only', titleKey: 'admin.features.items.bot.twitchHelixOnly.title', descriptionKey: 'admin.features.items.bot.twitchHelixOnly.description', groupKey: 'admin.features.groups.twitch', route: '/twitch-bots' }
  ];

  loading = false;
  flagsByKey: Record<string, AdminFeatureFlag> = {};
  savingKeys = new Set<string>();

  ngOnInit(): void {
    this.loadFeatureFlags();
    this.actionsSubscription = this.pageActions.actions$.subscribe(action => {
      if (action === 'features-refresh') {
        this.loadFeatureFlags();
      }
    });
  }

  ngOnDestroy(): void {
    this.actionsSubscription?.unsubscribe();
  }

  get groupedFeatures(): Array<{ groupKey: string; items: FeatureDefinition[] }> {
    const groups = new Map<string, FeatureDefinition[]>();
    for (const feature of this.features) {
      const items = groups.get(feature.groupKey) ?? [];
      items.push(feature);
      groups.set(feature.groupKey, items);
    }
    return Array.from(groups.entries()).map(([groupKey, items]) => ({ groupKey, items }));
  }

  loadFeatureFlags(): void {
    this.loading = true;
    this.api.listFeatureFlags().subscribe({
      next: (flags) => {
        this.flagsByKey = Object.fromEntries((flags ?? []).map(flag => [flag.key, flag]));
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(error, 'admin.features.loadError');
      }
    });
  }

  currentStatus(featureKey: string): AdminFeatureFlag['status'] {
    return this.flagsByKey[featureKey]?.status ?? 'ENABLED';
  }

  isStatusActive(featureKey: string, status: AdminFeatureFlag['status']): boolean {
    return this.currentStatus(featureKey) === status;
  }

  updateFeatureFlag(featureKey: string, status: AdminFeatureFlag['status']): void {
    if (this.isSaving(featureKey) || this.isStatusActive(featureKey, status)) {
      return;
    }

    this.savingKeys.add(featureKey);
    this.api.updateFeatureFlag(featureKey, status).subscribe({
      next: (flag) => {
        this.flagsByKey = {
          ...this.flagsByKey,
          [featureKey]: flag
        };
        this.savingKeys.delete(featureKey);
        this.toast.success(this.i18n.t('admin.features.updateSuccess'));
        this.loadFeatureFlags();
      },
      error: (error: HttpErrorResponse) => {
        this.savingKeys.delete(featureKey);
        this.handleError(error, 'admin.features.updateError');
      }
    });
  }

  isSaving(featureKey: string): boolean {
    return this.savingKeys.has(featureKey);
  }

  statusLabel(status: AdminFeatureFlag['status']): string {
    return `admin.features.status.${status}`;
  }

  private handleError(error: HttpErrorResponse, fallbackKey: string): void {
    const message = typeof error.error?.message === 'string' && error.error.message.trim()
      ? error.error.message.trim()
      : this.i18n.t(fallbackKey);
    this.toast.error(message);
  }
}
